import { useEffect } from 'react';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// OTA live updates are an Android-APK-only feature. In a browser (phone or
// desktop) this component is an inert no-op — the @capgo plugin has no native
// bridge there, and the website is updated by `npm run deploy:web` anyway.
const isAndroid = () => Capacitor.getPlatform() === 'android';

// Where the self-hosted manifest + bundles live, baked in at build time from
// REACT_APP_OTA_URL. NO trailing slash.
const OTA_BASE = (process.env.REACT_APP_OTA_URL || '').replace(/\/$/, '');

// "3.4.1" → [3,4,1]. True when a is strictly newer than b. A 2-segment version
// ("3.4", an older build.gradle) reads as 3.4.0.
const semverGt = (a, b) => {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
};

/**
 * OtaUpdater — self-hosted OTA live updates (`@capgo/capacitor-updater` in
 * MANUAL mode; the paid Capgo cloud is never called). Renders nothing.
 *
 * On start, and again on every resume:
 *   1. `notifyAppReady()` — THE MANDATORY CALL. It cancels the plugin's
 *      post-update rollback watchdog; without it an applied bundle is rolled
 *      back on the next launch. It must run on every boot.
 *   2. GET `${OTA_BASE}/latest.json` = `{ version, url, checksum }` through
 *      native `CapacitorHttp`, which bypasses the WebView's CORS.
 *   3. If that version is newer than the running bundle (floored at the version
 *      built into the APK), `download()` the zip — the plugin verifies it
 *      against the sha256 — and `next()` it, so it is applied on the NEXT COLD
 *      START, never mid-session. The plugin also refuses anything at or below
 *      the native version, so an OTA bundle can neither downgrade the app nor
 *      fight a Play release.
 *
 * Fail-open throughout: any network, parse or plugin error is swallowed, so an
 * OTA hiccup can never block or crash start-up. The plugin is pulled in with a
 * dynamic import() so a web build never resolves the native module.
 */
export default function OtaUpdater() {
  useEffect(() => {
    if (!isAndroid() || !OTA_BASE) return undefined;

    let cancelled = false;
    let resumeHandle;

    const checkForUpdate = async (CapacitorUpdater) => {
      try {
        // Cache-busted so a proxy can never pin an old manifest.
        const res = await CapacitorHttp.get({
          url: `${OTA_BASE}/latest.json?t=${Date.now()}`,
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (cancelled || res.status !== 200) return;
        let manifest = res.data;
        if (typeof manifest === 'string') {
          try { manifest = JSON.parse(manifest); } catch { return; }
        }
        if (cancelled || !manifest || !manifest.version || !manifest.url) return;

        // Floor = max(running bundle, native APK version) — never go below the
        // shipped APK even if latest.json is wrong.
        const builtin = await CapacitorUpdater.getBuiltinVersion().catch(() => ({ version: '0.0.0' }));
        const cur = await CapacitorUpdater.current();
        const curRaw = cur && cur.bundle && cur.bundle.version;
        const curVersion = (curRaw && curRaw !== 'builtin') ? curRaw : builtin.version;
        const floor = semverGt(curVersion, builtin.version) ? curVersion : builtin.version;
        if (!semverGt(manifest.version, floor)) return; // not strictly newer

        const bundle = await CapacitorUpdater.download({
          url: manifest.url,
          version: manifest.version,
          ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
        });
        if (cancelled) return;
        await CapacitorUpdater.next({ id: bundle.id }); // applied on next cold start
        console.log(`[OTA] downloaded ${manifest.version}; applies on next restart`);
      } catch (err) {
        console.warn('[OTA] update check skipped:', (err && err.message) || err);
      }
    };

    (async () => {
      try {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
        if (cancelled) return;

        await CapacitorUpdater.notifyAppReady(); // mandatory — cancels rollback
        await checkForUpdate(CapacitorUpdater);

        const h = await CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) checkForUpdate(CapacitorUpdater);
        });
        if (cancelled) { h.remove(); return; }
        resumeHandle = h;
      } catch (err) {
        console.warn('[OTA] updater init skipped:', (err && err.message) || err);
      }
    })();

    return () => {
      cancelled = true;
      if (resumeHandle) { try { resumeHandle.remove(); } catch { /* noop */ } }
    };
  }, []);

  return null;
}
